"use client";
import React, { useState } from "react";
import { motion } from "motion/react";
import { Mail, Phone, MapPin, Clock, Send } from "lucide-react";
import { useTranslation } from "react-i18next";
import { GlassCard } from "@/src/components/GlassCard";
import { Button } from "@/src/components/ui/button";

export const ContactPage: React.FC = () => {
  const { t } = useTranslation("common");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    message: "",
  });

  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
    setFormData({ name: "", email: "", company: "", phone: "", message: "" });
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center px-4 sm:px-6 lg:px-8 pt-24">
        {/* Glassmorphism Background with Gradient Orbs */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          {/* Animated gradient orbs */}
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-walnut-400/30 dark:bg-walnut-600/20 rounded-full blur-[100px] animate-pulse" />
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-beige-400/20 dark:bg-beige-800/10 rounded-full blur-[80px]" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-grey-300/20 dark:bg-grey-700/10 rounded-full blur-[90px]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto text-center py-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-block mb-6">
              <div className="backdrop-blur-xl bg-walnut-500/10 dark:bg-walnut-500/20 px-6 py-2 rounded-full border border-walnut-500/20 dark:border-walnut-400/30">
                <span className="text-walnut-700 dark:text-walnut-300 font-medium">
                  {t("contact.badge")}
                </span>
              </div>
            </div>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 text-grey-900 dark:text-white">
              {t("contact.title")}
            </h1>

            <p className="text-lg sm:text-xl text-grey-600 dark:text-grey-300 mb-8 max-w-3xl mx-auto">
              {t("contact.subtitle")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contact Information */}
            <div className="space-y-6">
              <GlassCard className="p-6">
                <div className="w-12 h-12 rounded-lg bg-walnut-100 dark:bg-walnut-800 flex items-center justify-center mb-4">
                  <MapPin className="w-6 h-6 text-walnut-700 dark:text-walnut-300" />
                </div>
                <h3 className="text-xl font-semibold text-walnut-900 dark:text-white mb-2">
                  {t("marketingPages.contact.visitUs")}
                </h3>
                <p className="text-walnut-700 dark:text-walnut-300">
                  123 Industrial Park
                  <br />
                  Manufacturing District
                  <br />
                  City, State 12345
                  <br />
                  United States
                </p>
              </GlassCard>

              <GlassCard className="p-6">
                <div className="w-12 h-12 rounded-lg bg-walnut-100 dark:bg-walnut-800 flex items-center justify-center mb-4">
                  <Phone className="w-6 h-6 text-walnut-700 dark:text-walnut-300" />
                </div>
                <h3 className="text-xl font-semibold text-walnut-900 dark:text-white mb-2">
                  {t("marketingPages.contact.callUs")}
                </h3>
                <p className="text-walnut-700 dark:text-walnut-300">
                  Main: +1 (555) 123-4567
                  <br />
                  Sales: +1 (555) 123-4568
                  <br />
                  Support: +1 (555) 123-4569
                </p>
              </GlassCard>

              <GlassCard className="p-6">
                <div className="w-12 h-12 rounded-lg bg-walnut-100 dark:bg-walnut-800 flex items-center justify-center mb-4">
                  <Mail className="w-6 h-6 text-walnut-700 dark:text-walnut-300" />
                </div>
                <h3 className="text-xl font-semibold text-walnut-900 dark:text-white mb-2">
                  {t("marketingPages.contact.emailUs")}
                </h3>
                <p className="text-walnut-700 dark:text-walnut-300">
                  General: info@walnutpackaging.com
                  <br />
                  Sales: sales@walnutpackaging.com
                  <br />
                  Support: support@walnutpackaging.com
                </p>
              </GlassCard>

              <GlassCard className="p-6">
                <div className="w-12 h-12 rounded-lg bg-walnut-100 dark:bg-walnut-800 flex items-center justify-center mb-4">
                  <Clock className="w-6 h-6 text-walnut-700 dark:text-walnut-300" />
                </div>
                <h3 className="text-xl font-semibold text-walnut-900 dark:text-white mb-2">
                  {t("marketingPages.contact.businessHours")}
                </h3>
                <p className="text-walnut-700 dark:text-walnut-300">
                  Monday - Friday: 8:00 AM - 6:00 PM
                  <br />
                  Saturday: 9:00 AM - 2:00 PM
                  <br />
                  Sunday: Closed
                </p>
              </GlassCard>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <GlassCard className="p-8">
                <h2 className="text-3xl font-bold text-walnut-900 dark:text-white mb-6">
                  {t("marketingPages.contact.sendMessageTitle")}
                </h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-walnut-900 dark:text-white mb-2"
                      >
                        {t("marketingPages.contact.name")}
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-lg backdrop-blur-xl bg-white/20 dark:bg-black/20 border border-white/30 dark:border-white/10 text-walnut-900 dark:text-white placeholder-walnut-600 dark:placeholder-walnut-400 focus:outline-none focus:border-walnut-500"
                        placeholder={t("marketingPages.contact.namePlaceholder")}
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-walnut-900 dark:text-white mb-2"
                      >
                        {t("marketingPages.contact.email")}
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-lg backdrop-blur-xl bg-white/20 dark:bg-black/20 border border-white/30 dark:border-white/10 text-walnut-900 dark:text-white placeholder-walnut-600 dark:placeholder-walnut-400 focus:outline-none focus:border-walnut-500"
                        placeholder={t("marketingPages.contact.emailPlaceholder")}
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label
                        htmlFor="company"
                        className="block text-walnut-900 dark:text-white mb-2"
                      >
                        {t("marketingPages.contact.company")}
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg backdrop-blur-xl bg-white/20 dark:bg-black/20 border border-white/30 dark:border-white/10 text-walnut-900 dark:text-white placeholder-walnut-600 dark:placeholder-walnut-400 focus:outline-none focus:border-walnut-500"
                        placeholder={t("marketingPages.contact.companyPlaceholder")}
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="phone"
                        className="block text-walnut-900 dark:text-white mb-2"
                      >
                        {t("marketingPages.contact.phone")}
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg backdrop-blur-xl bg-white/20 dark:bg-black/20 border border-white/30 dark:border-white/10 text-walnut-900 dark:text-white placeholder-walnut-600 dark:placeholder-walnut-400 focus:outline-none focus:border-walnut-500"
                        placeholder={t("marketingPages.contact.phonePlaceholder")}
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="message"
                      className="block text-walnut-900 dark:text-white mb-2"
                    >
                      {t("marketingPages.contact.message")}
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="w-full px-4 py-3 rounded-lg backdrop-blur-xl bg-white/20 dark:bg-black/20 border border-white/30 dark:border-white/10 text-walnut-900 dark:text-white placeholder-walnut-600 dark:placeholder-walnut-400 focus:outline-none focus:border-walnut-500 resize-none"
                      placeholder={t("marketingPages.contact.messagePlaceholder")}
                    />
                  </div>

                  <Button
                    type="submit"
                    variant="gradient"
                    size="lg"
                    className="w-full md:w-auto hover:scale-105"
                  >
                    {submitted
                      ? t("marketingPages.contact.messageSent")
                      : t("marketingPages.contact.sendMessage")}
                    <Send className="w-5 h-5" />
                  </Button>

                  {submitted && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-4 rounded-lg bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700 text-green-800 dark:text-green-200"
                    >
                      {t("marketingPages.contact.success")}
                    </motion.div>
                  )}
                </form>
              </GlassCard>

              {/* Map Placeholder */}
              <GlassCard className="p-0 overflow-hidden mt-8">
                <div className="w-full h-64 bg-gradient-to-br from-walnut-200 to-walnut-300 dark:from-walnut-800 dark:to-walnut-900 flex items-center justify-center">
                  <div className="text-center">
                    <MapPin className="w-12 h-12 text-walnut-600 dark:text-walnut-400 mx-auto mb-2" />
                    <p className="text-walnut-700 dark:text-walnut-300">
                      {t("marketingPages.contact.mapPlaceholder")}
                    </p>
                  </div>
                </div>
              </GlassCard>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
